import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import { offersAPI } from '../../lib/api';
import { getScoreColor } from '../../lib/scoreUtils';

export default function CandidatePipelineTab({ candidate, candidateId, parsed, match, scoreBreakdown, matchedSkillsSet, depthAnalysis, analyzingDepth, runDepthAnalysis, offerProb, pipelineHistory, offer, showOfferForm, setShowOfferForm, offerForm, setOfferForm, onboardingPlan, setOnboardingPlan, loadCandidate }) {
  return (
        <TabsContent value="pipeline">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Offer Probability */}
            {offerProb && (
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Card style={{ flex: 1, background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: offerProb.offer_probability === 'high' ? '#059669' : offerProb.offer_probability === 'medium' ? '#D97706' : '#DC2626', textTransform: 'uppercase' }}>
                    {offerProb.offer_probability}
                  </div>
                  <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>Hire Probability</div>
                </Card>
                <Card style={{ flex: 1, background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', textAlign: 'center' }}>
                  <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 700, color: '#0076A3' }}>{offerProb.composite_score}%</div>
                  <div style={{ fontSize: '0.6875rem', color: '#64748B' }}>Composite Score</div>
                </Card>
              </div>
            )}

            {/* Current Stage */}
            <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.75rem' }}>Pipeline Stage</h4>
              {(() => {
                const el = candidate.stage_eligibility;
                if (!el || !el.next_stage || el.next_stage === 'hired') {
                  return (
                    <div data-testid="stage-eligibility-banner" style={{ padding: '0.75rem 1rem', borderRadius: 10, background: '#F1EAE1', color: '#64748B', fontSize: '0.75rem', marginBottom: '1rem' }}>
                      {el && el.next_stage === 'hired'
                        ? 'Final hiring decision is manual — advance to "hired" when ready.'
                        : 'No further automated stage available.'}
                    </div>
                  );
                }
                const advanceStage = async (stage) => {
                  try {
                    await offersAPI.updateStage(candidateId, { stage, notes: '' });
                    toast.success(stage === 'rejected' ? 'Candidate rejected' : `Advanced to ${stage}`);
                    loadCandidate();
                  } catch (e) { toast.error('Failed to update stage'); }
                };
                const meets = el.eligible === true;
                const below = el.eligible === false;
                const manual = el.benchmark === null || el.benchmark === undefined;
                const pending = el.eligible === null && !manual;
                const bg = meets ? '#F0FDF4' : below ? '#FEF3C7' : '#F1EAE1';
                const bd = meets ? '#86EFAC' : below ? '#FCD34D' : '#ECE7DE';
                const fg = meets ? '#059669' : below ? '#B45309' : '#64748B';
                const nextLabel = ({ bgv: 'BGV', credentials: 'Credentials' }[el.next_stage]) || el.next_stage;
                return (
                  <div style={{ marginBottom: '1rem' }}>
                    <div data-testid="stage-eligibility-banner" style={{ padding: '0.75rem 1rem', borderRadius: 10, background: bg, border: `1px solid ${bd}`, fontSize: '0.75rem', color: fg, marginBottom: '0.75rem', textTransform: 'none' }}>
                      {manual && <span><strong>Manual stage.</strong> Moving to <span style={{ textTransform: 'capitalize' }}>{nextLabel}</span> is a recruiter decision (no automatic benchmark).</span>}
                      {pending && <span><strong>Awaiting score</strong> — no {el.score_label?.toLowerCase() || 'score'} yet to evaluate against the {nextLabel} benchmark ({el.benchmark}).</span>}
                      {meets && <span><strong>✓ Eligible to advance.</strong> {el.score_label} {el.current_score} meets the {nextLabel} benchmark of {el.benchmark}.</span>}
                      {below && <span><strong>Below benchmark.</strong> {el.score_label} {el.current_score} is under the {nextLabel} benchmark of {el.benchmark}. You can still advance manually.</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button data-testid="advance-stage-button" onClick={() => advanceStage(el.next_stage)} style={{ background: meets ? '#059669' : '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}>
                        {meets ? `Advance to ${nextLabel}` : `Advance to ${nextLabel}${manual ? '' : ' (manual)'}`}
                      </Button>
                      {candidate.pipeline_stage !== 'rejected' && (
                        <Button data-testid="reject-candidate-button" variant="outline" onClick={() => advanceStage('rejected')} style={{ borderColor: '#FCA5A5', color: '#DC2626', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600 }}>
                          Reject
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {['applied', 'screening', 'assessment', 'interview', 'bgv', 'credentials', 'offer', 'hired'].map(stage => {
                  const STAGE_FLOW = ['applied', 'screening', 'assessment', 'interview', 'bgv', 'credentials', 'offer', 'hired'];
                  const current = candidate.pipeline_stage || 'applied';
                  const idx = STAGE_FLOW.indexOf(stage);
                  const curIdx = STAGE_FLOW.indexOf(current);
                  const isPast = idx < curIdx;
                  const isCurrent = stage === current;
                  const labelMap = { bgv: 'BGV', credentials: 'Credentials' };
                  return (
                    <button key={stage} onClick={async () => {
                      try {
                        await offersAPI.updateStage(candidateId, { stage, notes: '' });
                        toast.success(`Stage updated to ${stage}`);
                        loadCandidate();
                      } catch (e) { toast.error('Failed to update stage'); }
                    }} style={{
                      padding: '0.375rem 0.75rem', borderRadius: 999, border: 'none', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
                      background: isCurrent ? '#0076A3' : isPast ? '#E0F2FE' : '#F1EAE1',
                      color: isCurrent ? '#fff' : isPast ? '#005A80' : '#94A3B8',
                    }}>{labelMap[stage] || stage}</button>
                  );
                })}
              </div>
              {/* Stage history */}
              {pipelineHistory.length > 0 && (
                <div>
                  <h5 style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase', marginBottom: '0.5rem' }}>History</h5>
                  {pipelineHistory.slice(0, 5).map((e, i) => (
                    <div key={`pe-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', borderBottom: i < pipelineHistory.length - 1 ? '1px solid #F1F5F9' : 'none', fontSize: '0.75rem' }}>
                      <span style={{ color: '#94A3B8', width: 70 }}>{new Date(e.created_at).toLocaleDateString()}</span>
                      <span style={{ color: '#DC2626' }}>{e.from_stage}</span>
                      <span style={{ color: '#94A3B8' }}>→</span>
                      <span style={{ color: '#059669', fontWeight: 600 }}>{e.to_stage}</span>
                      <span style={{ color: '#94A3B8', marginLeft: 'auto' }}>{e.changed_by_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Offer Section */}
            <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3' }}>Offer</h4>
                {!offer && <Button onClick={() => setShowOfferForm(true)} style={{ background: '#059669', color: '#fff', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600 }}>Create Offer</Button>}
              </div>

              {offer ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ padding: '0.75rem', borderRadius: 10, background: '#F0FDF4', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>{offer.currency} {offer.ctc}</div>
                      <div style={{ fontSize: '0.625rem', color: '#64748B' }}>CTC</div>
                    </div>
                    <div style={{ padding: '0.75rem', borderRadius: 10, background: '#FFE7D9', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3' }}>{offer.joining_date || 'TBD'}</div>
                      <div style={{ fontSize: '0.625rem', color: '#64748B' }}>Joining Date</div>
                    </div>
                    <div style={{ padding: '0.75rem', borderRadius: 10, background: '#FAF7F2', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3', textTransform: 'capitalize' }}>{offer.status}</div>
                      <div style={{ fontSize: '0.625rem', color: '#64748B' }}>Status</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                    {offer.status === 'draft' && <Button onClick={async () => { await offersAPI.update(offer._id, { status: 'sent' }); toast.success('Offer sent!'); loadCandidate(); }} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem' }}>Send Offer</Button>}
                    {offer.status === 'sent' && <Button onClick={async () => { await offersAPI.update(offer._id, { status: 'accepted' }); toast.success('Offer accepted!'); loadCandidate(); }} style={{ background: '#059669', color: '#fff', borderRadius: 8, fontSize: '0.75rem' }}>Mark Accepted</Button>}
                    {offer.status === 'sent' && <Button onClick={async () => { await offersAPI.update(offer._id, { status: 'rejected' }); toast.error('Offer rejected'); loadCandidate(); }} variant="outline" style={{ borderRadius: 8, fontSize: '0.75rem', borderColor: '#FECACA', color: '#DC2626' }}>Mark Rejected</Button>}
                    {offer.status === 'accepted' && (
                      <Button onClick={async () => {
                        try { const r = await offersAPI.generateOnboarding(offer._id); setOnboardingPlan(r.data); toast.success('Onboarding plan generated!'); }
                        catch (e) { toast.error('Failed to generate plan'); }
                      }} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontSize: '0.75rem' }}>Generate Onboarding Plan</Button>
                    )}
                  </div>

                  {/* Onboarding Plan */}
                  {(onboardingPlan || offer.onboarding_plan) && (() => {
                    const plan = onboardingPlan || offer.onboarding_plan;
                    return (
                      <div style={{ marginTop: '1rem' }}>
                        <h5 style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0076A3', marginBottom: '0.75rem' }}>30-Day Onboarding Plan</h5>
                        {(plan.weeks || []).map((w, wi) => (
                          <div key={`week-${wi}`} style={{ marginBottom: '0.75rem', padding: '0.75rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
                            <div style={{ fontWeight: 600, color: '#0076A3', fontSize: '0.8125rem', marginBottom: '0.375rem' }}>Week {w.week}: {w.theme}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: '#4A5568' }}>
                              {(w.goals || []).map((g, gi) => <div key={`g-${wi}-${gi}`}>• {g}</div>)}
                            </div>
                          </div>
                        ))}
                        {(plan.key_milestones || []).length > 0 && (
                          <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>
                            Milestones: {plan.key_milestones.join(' | ')}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : showOfferForm ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: '0.5rem' }}>
                    <input value={offerForm.ctc} onChange={e => setOfferForm({ ...offerForm, ctc: e.target.value })} placeholder="CTC amount" style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }} />
                    <select value={offerForm.currency} onChange={e => setOfferForm({ ...offerForm, currency: e.target.value })} style={{ padding: '0.5rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.875rem' }}>
                      <option value="USD">USD</option><option value="INR">INR</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                    </select>
                  </div>
                  <input type="date" value={offerForm.joining_date} onChange={e => setOfferForm({ ...offerForm, joining_date: e.target.value })} style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }} />
                  <input value={offerForm.notes} onChange={e => setOfferForm({ ...offerForm, notes: e.target.value })} placeholder="Notes (optional)" style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #CBD5E1', fontSize: '0.875rem', outline: 'none' }} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="outline" onClick={() => setShowOfferForm(false)} style={{ borderRadius: 8, fontSize: '0.75rem' }}>Cancel</Button>
                    <Button onClick={async () => {
                      try {
                        await offersAPI.create({ candidate_id: candidateId, job_id: candidate.job_id, ...offerForm, ctc: parseFloat(offerForm.ctc) || 0 });
                        toast.success('Offer created!'); setShowOfferForm(false); loadCandidate();
                      } catch (e) { toast.error('Failed to create offer'); }
                    }} style={{ background: '#059669', color: '#fff', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600 }}>Create Offer</Button>
                  </div>
                </div>
              ) : (
                <p style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>No offer created yet. Click "Create Offer" to start.</p>
              )}
            </Card>
          </div>
        </TabsContent>
  );
}